<template>
  <header class="header">
    <div
      :class="[
        'header__container',
        {'header__container--open': isMobileMenuOpen},
      ]">
      <app-link
        to="/"
        @click="closeNavigation">
        <img
          class="header__logo-img"
          width="134"
          height="50"
          alt="æScan logo"
          src="/logo-light.svg">
        <div
          class="header__logo-bg"
          aria-hidden="true"/>
      </app-link>

      <div class="header__right-controls">
        <div class="header__toggle-mobile">
          <theme-toggle/>
        </div>

        <div
          class="header__hamburger"
          @click="toggleNavigation">
          <app-icon
            v-if="isMobileMenuOpen"
            name="cross"
            :size="34"/>
          <app-icon
            v-else
            name="menu"
            :size="24"/>
        </div>
      </div>

      <the-navigation
        :class="[
          'header__navigation',
          {'header__navigation--open': isMobileMenuOpen},
        ]"/>

      <div class="header__toggle-desktop">
        <theme-toggle/>
      </div>

      <network-select
        :class="[
          'header__network-select',
          {'header__network-select--open': isMobileMenuOpen}]"/>

      <wallet-account-controls class="u-hidden-mobile"/>
    </div>
    <div
      v-if="isSyncing"
      class="header__warning">
      Some services are currently being synced and data accuracy might be affected. Please check again later.
    </div>
    <div
      v-if="!isOnline"
      class="header__warning">
      You are currently offline. Please check your connection.
    </div>
    <div
      v-if="nodeStatus === false"
      class="header__warning">
      The Node is currently unavailable. Please check again later.
    </div>
    <div
      v-if="middlewareStatus === false"
      class="header__warning">
      The Middleware is currently unavailable. Please check again later.
    </div>
    <div
      v-if="isMarketCapAvailable === false"
      class="header__warning">
      Market Cap data are currently not available. Fiat price might not be up to date. Please check again later.
    </div>
  </header>
</template>

<script setup>
import { useOnline } from '@vueuse/core'
import { MENU_HASH } from '@/utils/constants'

const route = useRoute()
const router = useRouter()

const isOnline = useOnline()
const { isMobileMenuOpen } = storeToRefs(useUiStore())
const { isSyncing, nodeStatus, middlewareStatus } = storeToRefs(useStatus())
const { isMarketCapAvailable } = storeToRefs(useMarketStatsStore())

onMounted(() => {
  window.addEventListener('resize', closeNavigation)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', closeNavigation)
})

watch(route, () => {
  if (route.hash !== MENU_HASH) {
    closeNavigation()
  }
})
watch(() => route.fullPath, () => {
  if (route.hash !== MENU_HASH) {
    closeNavigation()
  }
})

function toggleNavigation() {
  if (!isMobileMenuOpen.value && router.options.history.state.back === null) {
    router.push({ hash: MENU_HASH })
  }

  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

function closeNavigation() {
  isMobileMenuOpen.value = false
}
</script>

<style scoped>
.header {
  background: var(--color-surface);
  display: flex;
  flex-direction: column;

  &__container {
    height: 100%;
    width: 100%;

    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;

    margin: 0 auto;
    padding: var(--space-2) var(--space-3);
    column-gap: var(--space-5);

    &--open {
      padding-bottom: var(--space-6);
    }

    @media (--desktop) {
      padding: var(--space-3) 0;
      max-width: var(--container-width);
    }
  }

  &__navigation {
    display: none;
    flex-basis: 100%;
    flex-grow: 1;

    &--open {
      margin: var(--space-6) 0 0;
      display: block;
    }

    @media (--desktop) {
      height: 100%;
      flex-basis: auto;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }
  }

  &__network-select {
    display: none;
    margin: var(--space-2) auto var(--space-5);

    &--open {
      display: block;
    }

    @media (--desktop) {
      margin: 0;
      display: block;
    }
  }

  &__right-controls {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    margin-left: auto;

    @media (--desktop) {
      display: none;
    }
  }

  &__toggle-mobile {
    display: flex;

    @media (--desktop) {
      display: none;
    }
  }

  &__toggle-desktop {
    display: none;

    @media (--desktop) {
      display: flex;
    }
  }

  &__hamburger {
    display: flex;
    cursor: pointer;
    color: var(--color-midnight);

    @media (--desktop) {
      display: none;
    }
  }

  &__icon {
    margin-left: var(--space-1);
  }

  &__warning {
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--color-fire);
    color: var(--color-white);
    font-family: var(--font-monospaced);
    padding: var(--space-0) var(--space-3);
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.0015em;
  }

  &__logo-img {
    display: none;
  }

  &__logo-bg {
    width: 134px;
    height: 50px;
    background-image: var(--logo-url);
    background-size: contain;
    background-repeat: no-repeat;
  }
}
</style>
